import { MediaSortOrder } from "@next-platform/packages-ts-types";
import { createSlice } from "@reduxjs/toolkit";
import { getMyMediaFiles } from "../services/galleryThunks";
import { GalleryFlow } from "../types/types";
const initialState = {
    files: [],
    filters: {},
    sort: MediaSortOrder.LATEST,
    nextCursor: null,
    requestId: null,
    hasLoadedFirstPage: false,
    resetCount: 0,
    loading: false,
    loadingType: null,
    error: null,
};
// The cursor tells the two flows apart: the first page has none, every later page carries one.
const getRequestFlow = (request) => request?.cursor ? GalleryFlow.GET_MORE_MEDIA_FILES : GalleryFlow.GET_MEDIA_FILES;
export const myGallerySlice = createSlice({
    name: "myGallery",
    initialState,
    reducers: {
        setMyGalleryFilters: (state, action) => {
            state.filters = action.payload;
        },
        setMyGallerySort: (state, action) => {
            state.sort = action.payload;
        },
        clearMyGalleryError: (state) => {
            state.error = null;
        },
        resetMyGallery: (state) => ({ ...initialState, resetCount: state.resetCount + 1 }),
    },
    extraReducers: (builder) => {
        builder.addCase(getMyMediaFiles.pending, (state, action) => {
            state.requestId = action.meta.requestId;
            state.loading = true;
            state.loadingType = getRequestFlow(action.meta.arg);
            state.error = null;
            // A first page starts a new listing, so pagination stays off until it lands.
            if (!action.meta.arg?.cursor) {
                state.nextCursor = null;
            }
        });
        builder.addCase(getMyMediaFiles.fulfilled, (state, action) => {
            if (action.meta.requestId !== state.requestId) {
                return;
            }
            if (action.meta.arg?.cursor) {
                const knownIds = new Set(state.files.map((file) => file.id));
                state.files.push(...action.payload.items.filter((file) => !knownIds.has(file.id)));
            }
            else {
                state.files = action.payload.items;
            }
            // A missing cursor must read as the end of the list, or hasMore would stay true forever.
            state.nextCursor = action.payload.next_cursor ?? null;
            state.hasLoadedFirstPage = true;
            state.loading = false;
            state.loadingType = null;
            state.requestId = null;
        });
        builder.addCase(getMyMediaFiles.rejected, (state, action) => {
            if (action.meta.requestId !== state.requestId) {
                return;
            }
            const errorType = getRequestFlow(action.meta.arg);
            const errorPayload = action.payload;
            state.loading = false;
            state.loadingType = errorType;
            state.requestId = null;
            state.error = { errorType, errorMessage: errorPayload?.message, errorCode: errorPayload?.errorCode };
        });
    },
});
export const { clearMyGalleryError, resetMyGallery, setMyGalleryFilters, setMyGallerySort } = myGallerySlice.actions;
export default myGallerySlice.reducer;
